import { useMutation, useQueryClient } from '@tanstack/react-query'
import { stepService } from '../api/stepService'
import { WAR_ROOM_QUERY_KEYS } from '../api/queryClient'
import type { Step, StepStatus } from '../types/session.types'

export interface UpdateStepPayload {
  stepId: string
  status: StepStatus
  note?: string
}

/**
 * Step status update with optimistic UI.
 * Updates local cache immediately for snappy feel during Go-Live
 * (WS event will also trigger re-fetch as confirmation).
 */
export function useUpdateStepStatus(sessionId: string) {
  const qc = useQueryClient()
  const queryKey = WAR_ROOM_QUERY_KEYS.steps(sessionId)

  return useMutation({
    mutationFn: ({ stepId, status, note }: UpdateStepPayload) =>
      stepService.updateStatus(sessionId, stepId, { status, note }),

    // ── Optimistic update ───────────────────────────────────────────────
    onMutate: async ({ stepId, status, note }) => {
      // Cancel in-flight refetches
      await qc.cancelQueries({ queryKey })

      // Snapshot previous value
      const snapshot = qc.getQueryData<Step[]>(queryKey)

      // Apply optimistic update
      qc.setQueryData<Step[]>(queryKey, (steps) => {
        if (!steps) return steps
        return steps.map((s) =>
          s.id === stepId
            ? {
                ...s,
                status,
                note: note ?? s.note,
                started_at:
                  status === 'in_progress' && !s.started_at
                    ? new Date().toISOString()
                    : s.started_at,
                completed_at:
                  ['done', 'failed', 'skipped'].includes(status)
                    ? new Date().toISOString()
                    : s.completed_at,
              }
            : s
        )
      })

      return { snapshot }
    },

    // ── Rollback on error ───────────────────────────────────────────────
    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        qc.setQueryData(queryKey, context.snapshot)
      }
    },

    // ── Confirm with server data ────────────────────────────────────────
    onSuccess: (response) => {
      qc.setQueryData<Step[]>(queryKey, (steps) => {
        if (!steps) return steps
        return steps.map((s) =>
          s.id === response.data.id ? response.data : s
        )
      })
    },

    onSettled: () => {
      // Final re-fetch to ensure consistency
      qc.invalidateQueries({ queryKey, exact: true })
    },
  })
}
